import React, { useCallback, useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VerifiedIcon from "@mui/icons-material/Verified";
import DownloadIcon from "@mui/icons-material/Download";
import {
    createAppraisal,
    createCpdRecord,
    createDeployment,
    createPayrollItem,
    createQualification,
    deleteCpdRecord,
    deleteDeployment,
    deletePayrollItem,
    deleteQualification,
    downloadPayslipPdf,
    fetchTeacherHrSummary,
    generatePayslip,
    uploadQualificationDocument,
    verifyQualification,
    verifyTscNumber,
} from "../../../utils/teacherHrApi";
import { currentSalaryPeriod } from "../../../utils/salaryPeriod";

const CPD_TYPES = ["Workshop", "Conference", "Online course", "In-service", "Other"];
const DEPLOYMENT_TYPES = ["Initial Posting", "Transfer", "Secondment", "Promotion", "Other"];
const RATINGS = ["Outstanding", "Good", "Satisfactory", "Needs improvement"];
const formatMWK = (n) => "MWK " + Number(n || 0).toLocaleString();

export default function TeacherHrPanel({ teacherId, schoolId, adminId, onTscUpdated }) {
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [message, setMessage] = useState("");
    const [dialog, setDialog] = useState(null);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);

    const load = useCallback(async () => {
        if (!teacherId) return;
        setLoading(true);
        try {
            const summary = await fetchTeacherHrSummary(teacherId);
            setData(summary);
        } catch (err) {
            setMessage(err.response?.data?.message || err.message || "Could not load HR data.");
        } finally {
            setLoading(false);
        }
    }, [teacherId]);

    useEffect(() => {
        load();
    }, [load]);

    const teacher = data?.teacher;
    const summary = data?.summary;

    const handleTscVerify = async (status) => {
        try {
            await verifyTscNumber(teacherId, status);
            setMessage(`TSC marked as ${status}.`);
            await load();
            onTscUpdated?.();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not update TSC.");
        }
    };

    const openDialog = (type, defaults = {}) => {
        setForm(defaults);
        setUploadFile(null);
        setDialog(type);
    };

    const handleSave = async () => {
        setSaving(true);
        setMessage("");
        try {
            if (dialog === "qualification") {
                let documentUrl = form.documentUrl || "";
                if (uploadFile) {
                    const up = await uploadQualificationDocument(teacherId, schoolId, uploadFile);
                    documentUrl = up.documentUrl;
                }
                await createQualification(teacherId, { ...form, schoolId, documentUrl });
            } else if (dialog === "deployment") {
                await createDeployment(teacherId, { ...form, schoolId, recordedById: adminId });
            } else if (dialog === "cpd") {
                await createCpdRecord(teacherId, { ...form, schoolId });
            } else if (dialog === "appraisal") {
                await createAppraisal(teacherId, { ...form, schoolId, appraiserId: adminId });
            } else if (dialog === "payroll") {
                await createPayrollItem(teacherId, { ...form, schoolId });
            } else if (dialog === "payslip") {
                await generatePayslip(teacherId, form.period || currentSalaryPeriod());
            }
            setDialog(null);
            await load();
            setMessage("Saved.");
        } catch (err) {
            setMessage(err.response?.data?.message || err.message || "Save failed.");
        } finally {
            setSaving(false);
        }
    };

    if (loading && !data) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {message ? (
                <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMessage("")}>
                    {message}
                </Alert>
            ) : null}

            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                <Chip label={`CPD ${summary?.cpdHoursTotal ?? 0} hrs`} color="primary" variant="outlined" />
                <Chip label={`${summary?.verifiedQualifications ?? 0}/${summary?.qualificationsCount ?? 0} quals verified`} variant="outlined" />
                <Chip
                    label={`TSC: ${teacher?.tscVerificationStatus || "Pending"}`}
                    color={teacher?.tscVerificationStatus === "Verified" ? "success" : "warning"}
                />
                {summary?.payrollPreview ? (
                    <Chip label={`Net preview ${formatMWK(summary.payrollPreview.netPay)}`} variant="outlined" />
                ) : null}
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Button size="small" startIcon={<VerifiedIcon />} onClick={() => handleTscVerify("Verified")}>
                    Verify TSC
                </Button>
                <Button size="small" color="warning" onClick={() => handleTscVerify("Rejected")}>
                    Reject TSC
                </Button>
            </Stack>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}>
                <Tab label="Qualifications" />
                <Tab label="Deployment" />
                <Tab label="CPD" />
                <Tab label="Appraisal" />
                <Tab label="Payroll & payslips" />
                <Tab label="Leave history" />
            </Tabs>

            {tab === 0 && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Typography fontWeight={700}>Qualifications & certificates</Typography>
                        <Button size="small" startIcon={<AddIcon />} onClick={() => openDialog("qualification", { qualificationType: "Certificate" })}>
                            Add
                        </Button>
                    </Stack>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Title</TableCell>
                                <TableCell>Institution</TableCell>
                                <TableCell>Year</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(teacher?.qualificationsList || []).map((q) => (
                                <TableRow key={q._id || q.id}>
                                    <TableCell>{q.title}</TableCell>
                                    <TableCell>{q.institution || "—"}</TableCell>
                                    <TableCell>{q.yearObtained || "—"}</TableCell>
                                    <TableCell>
                                        <Chip size="small" label={q.verificationStatus} color={q.verificationStatus === "Verified" ? "success" : "default"} />
                                    </TableCell>
                                    <TableCell align="right">
                                        {q.documentUrl ? (
                                            <Button size="small" href={q.documentUrl} target="_blank" rel="noreferrer">
                                                View
                                            </Button>
                                        ) : null}
                                        {q.verificationStatus === "Pending" ? (
                                            <Button
                                                size="small"
                                                onClick={async () => {
                                                    await verifyQualification(q._id || q.id, { status: "Verified", verifiedById: adminId });
                                                    await load();
                                                }}
                                            >
                                                Verify
                                            </Button>
                                        ) : null}
                                        <Button size="small" color="error" onClick={async () => { await deleteQualification(q._id || q.id); await load(); }}>
                                            Delete
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            )}

            {tab === 1 && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Typography fontWeight={700}>Deployment & transfer history</Typography>
                        <Button size="small" startIcon={<AddIcon />} onClick={() => openDialog("deployment", { deploymentType: "Transfer" })}>
                            Record
                        </Button>
                    </Stack>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Type</TableCell>
                                <TableCell>From → To</TableCell>
                                <TableCell>Effective</TableCell>
                                <TableCell>Ref</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(teacher?.deployments || []).map((d) => (
                                <TableRow key={d._id || d.id}>
                                    <TableCell>{d.deploymentType}</TableCell>
                                    <TableCell>
                                        {d.fromPost || d.fromSchool || "—"} → {d.toPost || d.toSchool || "—"}
                                    </TableCell>
                                    <TableCell>{d.effectiveDate ? new Date(d.effectiveDate).toLocaleDateString() : "—"}</TableCell>
                                    <TableCell>{d.referenceNumber || "—"}</TableCell>
                                    <TableCell align="right">
                                        <Button size="small" color="error" onClick={async () => { await deleteDeployment(d._id || d.id); await load(); }}>
                                            Delete
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            )}

            {tab === 2 && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Typography fontWeight={700}>CPD records ({summary?.cpdHoursTotal ?? 0} hours total)</Typography>
                        <Button size="small" startIcon={<AddIcon />} onClick={() => openDialog("cpd", { cpdType: "Workshop", hours: "8" })}>
                            Add CPD
                        </Button>
                    </Stack>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Title</TableCell>
                                <TableCell>Provider</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell align="right">Hours</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(teacher?.cpdRecords || []).map((c) => (
                                <TableRow key={c._id || c.id}>
                                    <TableCell>{c.title}</TableCell>
                                    <TableCell>{c.provider || "—"}</TableCell>
                                    <TableCell>{c.cpdType}</TableCell>
                                    <TableCell align="right">{c.hours}</TableCell>
                                    <TableCell align="right">
                                        <Button size="small" color="error" onClick={async () => { await deleteCpdRecord(c._id || c.id); await load(); }}>
                                            Delete
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            )}

            {tab === 3 && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Typography fontWeight={700}>Performance appraisal</Typography>
                        <Button size="small" startIcon={<AddIcon />} onClick={() => openDialog("appraisal", { rating: "Satisfactory", status: "Draft" })}>
                            New appraisal
                        </Button>
                    </Stack>
                    {(teacher?.appraisals || []).map((a) => (
                        <Paper key={a._id || a.id} variant="outlined" sx={{ p: 2, mb: 1.5, borderRadius: 2 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                <Typography fontWeight={700}>{a.periodLabel || a.academicYear || "Appraisal"}</Typography>
                                <Chip size="small" label={a.rating} />
                                <Chip size="small" variant="outlined" label={a.status} />
                            </Stack>
                            {a.strengths ? <Typography variant="body2"><strong>Strengths:</strong> {a.strengths}</Typography> : null}
                            {a.developmentAreas ? <Typography variant="body2"><strong>Development:</strong> {a.developmentAreas}</Typography> : null}
                            {a.actionPlan ? <Typography variant="body2"><strong>Action plan:</strong> {a.actionPlan}</Typography> : null}
                        </Paper>
                    ))}
                </Paper>
            )}

            {tab === 4 && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Typography fontWeight={700}>Salary structure</Typography>
                        <Stack direction="row" spacing={1}>
                            <Button size="small" startIcon={<AddIcon />} onClick={() => openDialog("payroll", { itemType: "Allowance", amount: "0" })}>
                                Allowance/Deduction
                            </Button>
                            <Button size="small" variant="contained" onClick={() => openDialog("payslip", { period: currentSalaryPeriod() })}>
                                Generate payslip
                            </Button>
                        </Stack>
                    </Stack>
                    <Table size="small" sx={{ mb: 2 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Type</TableCell>
                                <TableCell>Label</TableCell>
                                <TableCell align="right">Amount</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(teacher?.payrollItems || []).map((p) => (
                                <TableRow key={p._id || p.id}>
                                    <TableCell>{p.itemType}</TableCell>
                                    <TableCell>{p.label}</TableCell>
                                    <TableCell align="right">
                                        {p.isPercent ? `${p.amount}%` : formatMWK(p.amount)}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Button size="small" color="error" onClick={async () => { await deletePayrollItem(p._id || p.id); await load(); }}>
                                            Remove
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <Typography fontWeight={700} gutterBottom>Payslips</Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Period</TableCell>
                                <TableCell align="right">Net pay</TableCell>
                                <TableCell align="right">PDF</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(teacher?.payslips || []).map((p) => (
                                <TableRow key={p._id || p.id}>
                                    <TableCell>{p.period}</TableCell>
                                    <TableCell align="right">{formatMWK(p.netPay)}</TableCell>
                                    <TableCell align="right">
                                        <Button size="small" startIcon={<DownloadIcon />} onClick={() => downloadPayslipPdf(p._id || p.id)}>
                                            PDF
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            )}

            {tab === 5 && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography fontWeight={700} gutterBottom>Recent leave applications</Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Type</TableCell>
                                <TableCell>Dates</TableCell>
                                <TableCell>Days</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(teacher?.leaves || []).map((l) => (
                                <TableRow key={l._id || l.id}>
                                    <TableCell>{l.leaveType}</TableCell>
                                    <TableCell>
                                        {new Date(l.startDate).toLocaleDateString()} – {new Date(l.endDate).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell>{l.numberOfDays}</TableCell>
                                    <TableCell><Chip size="small" label={l.status} /></TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            )}

            <Dialog open={Boolean(dialog)} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {dialog === "qualification" && "Add qualification"}
                    {dialog === "deployment" && "Record deployment"}
                    {dialog === "cpd" && "Add CPD record"}
                    {dialog === "appraisal" && "New appraisal"}
                    {dialog === "payroll" && "Payroll line item"}
                    {dialog === "payslip" && "Generate payslip"}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {dialog === "qualification" && (
                            <>
                                <TextField label="Title" fullWidth size="small" value={form.title || ""} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
                                <TextField label="Institution" fullWidth size="small" value={form.institution || ""} onChange={(e) => setForm((f) => ({ ...f, institution: e.target.value }))} />
                                <TextField label="Year obtained" fullWidth size="small" value={form.yearObtained || ""} onChange={(e) => setForm((f) => ({ ...f, yearObtained: e.target.value }))} />
                                <Button variant="outlined" component="label" size="small">
                                    Upload certificate
                                    <input type="file" hidden accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} />
                                </Button>
                                {uploadFile ? <Typography variant="caption">{uploadFile.name}</Typography> : null}
                            </>
                        )}
                        {dialog === "deployment" && (
                            <>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Type</InputLabel>
                                    <Select label="Type" value={form.deploymentType || ""} onChange={(e) => setForm((f) => ({ ...f, deploymentType: e.target.value }))}>
                                        {DEPLOYMENT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <TextField label="From post / school" fullWidth size="small" value={form.fromPost || ""} onChange={(e) => setForm((f) => ({ ...f, fromPost: e.target.value }))} />
                                <TextField label="To post / school" fullWidth size="small" value={form.toPost || ""} onChange={(e) => setForm((f) => ({ ...f, toPost: e.target.value }))} />
                                <TextField label="Effective date" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.effectiveDate || ""} onChange={(e) => setForm((f) => ({ ...f, effectiveDate: e.target.value }))} />
                                <TextField label="TSC / reference no." fullWidth size="small" value={form.referenceNumber || ""} onChange={(e) => setForm((f) => ({ ...f, referenceNumber: e.target.value }))} />
                            </>
                        )}
                        {dialog === "cpd" && (
                            <>
                                <TextField label="Title" fullWidth size="small" value={form.title || ""} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
                                <TextField label="Provider" fullWidth size="small" value={form.provider || ""} onChange={(e) => setForm((f) => ({ ...f, provider: e.target.value }))} />
                                <FormControl fullWidth size="small">
                                    <InputLabel>Type</InputLabel>
                                    <Select label="Type" value={form.cpdType || ""} onChange={(e) => setForm((f) => ({ ...f, cpdType: e.target.value }))}>
                                        {CPD_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <TextField label="Hours" type="number" fullWidth size="small" value={form.hours || ""} onChange={(e) => setForm((f) => ({ ...f, hours: e.target.value }))} />
                                <TextField label="Completion date" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.completionDate || ""} onChange={(e) => setForm((f) => ({ ...f, completionDate: e.target.value }))} />
                            </>
                        )}
                        {dialog === "appraisal" && (
                            <>
                                <TextField label="Period label" fullWidth size="small" value={form.periodLabel || ""} onChange={(e) => setForm((f) => ({ ...f, periodLabel: e.target.value }))} placeholder="e.g. Term 3 2025" />
                                <TextField label="Academic year" fullWidth size="small" value={form.academicYear || ""} onChange={(e) => setForm((f) => ({ ...f, academicYear: e.target.value }))} />
                                <FormControl fullWidth size="small">
                                    <InputLabel>Rating</InputLabel>
                                    <Select label="Rating" value={form.rating || ""} onChange={(e) => setForm((f) => ({ ...f, rating: e.target.value }))}>
                                        {RATINGS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <TextField label="Strengths" fullWidth multiline minRows={2} value={form.strengths || ""} onChange={(e) => setForm((f) => ({ ...f, strengths: e.target.value }))} />
                                <TextField label="Development areas" fullWidth multiline minRows={2} value={form.developmentAreas || ""} onChange={(e) => setForm((f) => ({ ...f, developmentAreas: e.target.value }))} />
                                <TextField label="Action plan" fullWidth multiline minRows={2} value={form.actionPlan || ""} onChange={(e) => setForm((f) => ({ ...f, actionPlan: e.target.value }))} />
                            </>
                        )}
                        {dialog === "payroll" && (
                            <>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Type</InputLabel>
                                    <Select label="Type" value={form.itemType || "Allowance"} onChange={(e) => setForm((f) => ({ ...f, itemType: e.target.value }))}>
                                        <MenuItem value="Allowance">Allowance</MenuItem>
                                        <MenuItem value="Deduction">Deduction</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField label="Label" fullWidth size="small" value={form.label || ""} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} />
                                <TextField label="Amount (MWK or % if percent)" type="number" fullWidth size="small" value={form.amount || ""} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
                            </>
                        )}
                        {dialog === "payslip" && (
                            <TextField label="Period (YYYY-MM)" fullWidth size="small" value={form.period || currentSalaryPeriod()} onChange={(e) => setForm((f) => ({ ...f, period: e.target.value }))} />
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialog(null)}>Cancel</Button>
                    <Button variant="contained" disabled={saving} onClick={handleSave}>{saving ? "Saving…" : "Save"}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
